import React from "react";
import { useNavigate } from "react-router-dom";
import {  CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { getNumberLocale } from "src/utils/numberFormat";
import "./EmailStatusPopup.scss";

const CAMPAIGN_OVERVIEW_ROUTE = "/account/engagement/campaign-analytics";

interface EmailStatusPopupProps {
  type: "sent" | "scheduled";
  onClose: () => void;
  date?: string;
  time?: string;
}

const EmailStatusPopup: React.FC<EmailStatusPopupProps> = ({ type, onClose, date, time }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = getNumberLocale(i18n.language);

  const goToCampaignOverview = () => {
    onClose();
    navigate(CAMPAIGN_OVERVIEW_ROUTE);
  };

  const dateFormatter = {
    format: (date: Date) => {
      const dd = String(date.getDate()).padStart(2, "0");
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      return `${dd}-${mm}-${date.getFullYear()}`;
    },
  };

  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const formatDate = (dateStr?: string) => {
    if (!dateStr) {return "";}
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) {return dateStr;}
    return dateFormatter.format(date);
  };

  const formatTime = (timeStr?: string, dateStr?: string) => {
    if (timeStr) {
      const base = dateStr ? new Date(dateStr) : new Date();
      if (Number.isNaN(base.getTime())) {return timeStr;}
      const [hours, minutes] = timeStr.split(":").map(unit => Number(unit));
      if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {return timeStr;}
      base.setHours(hours, minutes, 0, 0);
      return timeFormatter.format(base);
    } else if (dateStr) {
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) {return "";}
      return timeFormatter.format(date);
    }
    return timeFormatter.format(new Date());
  };

  const getTitle = () => {
    if (type === "scheduled" && date && time) {
      const formattedDate = formatDate(date);
      const formattedTime = formatTime(time);
      return t("campaignFlow.emailStatusPopup.scheduledAt", {
        date: formattedDate,
        time: formattedTime,
      });
    } else if (type === "scheduled" && date) {
      const formattedDate = formatDate(date);
      const formattedTime = formatTime(undefined, date);
      return t("campaignFlow.emailStatusPopup.scheduledAt", {
        date: formattedDate,
        time: formattedTime,
      });
    } else if (type === "sent") {
      const now = new Date();
      const formattedDate = dateFormatter.format(now);
      const formattedTime = timeFormatter.format(now);
      return t("campaignFlow.emailStatusPopup.sentAt", {
        date: formattedDate,
        time: formattedTime,
      });
    }
    return t("campaignFlow.emailStatusPopup.defaultTitle");
  };

  const popupData = {
    title: getTitle(),
    logo: "/images/DpLogo.png",
    icon: "/Icons/Group 13.svg",
    buttonText: t("campaignFlow.emailStatusPopup.gotIt"),
  };

  return (
    <div className="email-status-popup">
      <div className="popup-card">
        <button
          className="close-btn"
          onClick={goToCampaignOverview}
          aria-label={t("campaignFlow.common.close")}
        >
          <CloseOutlined/>
        </button>

        <div className="logo-row">
          <img src={popupData.icon} alt={t("campaignFlow.common.popupIcon")} className="popup-icon" />
          <img src={popupData.logo} alt={t("campaignFlow.common.companyLogo")} className="popup-logo" />
        </div>

        <div className="popup-divider" />

        <h2 className="popup-title">{popupData.title}</h2>

        <button className="popup-btn" onClick={goToCampaignOverview}>
          {popupData.buttonText}
        </button>
      </div>
    </div>
  );
};

export default EmailStatusPopup;
